<template>
    <div class="client-search-container">
        <!-- Search Header -->
        <div class="search-header">
            <h3 class="search-title">
                <span class="icon">🔍</span> Client Search
            </h3>
            <div class="search-box">
                <input type="text" v-model="searchQuery" @keyup.enter="performSearch"
                    placeholder="Search by name, username, email or phone..." class="search-input"
                    :class="{ 'loading': isLoading }" />
                <button @click="performSearch" class="search-button" :disabled="!searchQuery || isLoading">
                    <span v-if="!isLoading">Search</span>
                    <span v-else class="spinner"></span>
                </button>
            </div>

            <!-- Filter Options -->
            <div class="filter-options" v-if="searchResults.length > 0">
                <select v-model="filterType" class="filter-select">
                    <option value="all">All Results</option>
                    <option value="withLocation">With Location Only</option>
                    <option value="recent">Most Recent</option>
                </select>
            </div>
        </div>

        <!-- Search Results -->
        <div class="search-results" v-if="searchResults.length > 0">
            <div class="results-header">
                <span class="results-count">{{ searchResults.length }} clients found</span>
                <span class="clear-results" @click="clearSearch" v-if="searchQuery">Clear</span>
            </div>

            <transition-group name="result-list" tag="div" class="results-list">
                <div v-for="client in filteredResults" :key="client.id" class="client-card"
                    :class="{ 'selected': selectedClientId === client.id }">
                    <!-- Client Avatar/Initials -->
                    <div class="client-avatar" :style="{ backgroundColor: getAvatarColor(client) }">
                        {{ getInitials(client.fullname_client) }}
                    </div>

                    <!-- Client Info -->
                    <div class="client-info">
                        <div class="client-name">{{ client.fullname_client }}</div>
                        <div class="client-username">@{{ client.username_client }} <span class="badge"
                                :style="{ backgroundColor: getAvatarColor(client) }">{{ client.recMarker }}</span></div>
                        <div class="client-details">
                            <span class="detail-item" v-if="client.email">
                                <span class="detail-icon">📧</span> {{ client.email }}
                            </span>
                            <span class="detail-item" v-if="client.phone">
                                <span class="detail-icon">📱</span> {{ client.mobileno_client }}
                            </span>
                            <span class="detail-item balance" :class="getBalanceClass(client.balance_client)">
                                <span class="detail-icon">💰</span> {{ Formatters.formatCurrency(client.balance_client)
                                }}
                            </span>
                        </div>
                        <div class="client-location" v-if="client.lat_client && client.lng_client">
                            <span class="location-icon">📍</span>
                            {{ client.lat_client.toFixed(4) }}, {{ client.lng_client.toFixed(4) }}
                        </div>
                        <div class="client-location no-location" v-else>
                            <span class="location-icon">🚫</span> No location data
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="client-actions">
                        <!-- Locate Button (only if has location) -->
                        <button v-if="client.lat_client && client.lng_client" @click="locateClient(client)"
                            class="action-button locate" title="Locate on map">
                            <span class="button-icon">📍</span>
                            <span class="button-text">Locate</span>
                        </button>

                        <!-- Pin Button (only if has location) -->
                        <button @click="pinClient(client)" class="action-button pin" title="Pin on map"
                            :class="{ 'active': isPinned(client.id) }">
                            <span class="button-icon">📌</span>
                            <span class="button-text">Pin</span>
                        </button>

                        <!-- View Profile Button -->
                        <button @click="viewProfile(client)" class="action-button profile" title="View profile">
                            <span class="button-icon">👤</span>
                            <span class="button-text">Profile</span>
                        </button>

                        <!-- More Actions Dropdown -->
                        <div class="more-actions">
                            <button @click="toggleMoreActions(client.id)" class="more-button">
                                ⋯
                            </button>
                            <div v-if="activeMoreId === client.id" class="dropdown-menu">
                                <button @click="viewTransactions(client)">📊 Transactions</button>
                                <button @click="sendMessage(client)">💬 Send Message</button>
                                <button @click="addToFavorites(client)">⭐ Add to Favorites</button>
                                <button @click="exportClientData(client)">📥 Export Data</button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>

            <!-- Load More Button -->
            <div class="load-more" v-if="hasMoreResults">
                <button @click="loadMore" class="load-more-button">
                    Load More Results
                </button>
            </div>
        </div>

        <!-- No Results State -->
        <div class="no-results" v-else-if="searchQuery && !isLoading">
            <div class="no-results-icon">🔍</div>
            <div class="no-results-text">No clients found matching "{{ searchQuery }}"</div>
            <div class="no-results-suggestions">
                Try:
                <ul>
                    <li>Checking spelling</li>
                    <li>Using fewer keywords</li>
                    <li>Searching by email or phone</li>
                </ul>
            </div>
        </div>

        <!-- Initial State -->
        <div class="initial-state" v-else-if="!searchQuery && !isLoading">
            <div class="initial-icon">👥</div>
            <div class="initial-text">Start typing to search for clients</div>
            <div class="recent-searches" v-if="recentSearches.length > 0">
                <div class="recent-title">Recent Searches:</div>
                <div class="recent-tags">
                    <span v-for="search in recentSearches" :key="search" @click="searchQuery = search; performSearch()"
                        class="recent-tag">
                        {{ search }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div class="loading-state" v-if="isLoading">
            <div class="spinner-large"></div>
            <div>Searching clients...</div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import clientService from '@/services/clientService'
import Formatters from '@/utils/formatters'

// Props
//eslint-disable-next-line
const props = defineProps({
    initialQuery: {
        type: String,
        default: ''
    },
    showFilters: {
        type: Boolean,
        default: true
    },
    maxResults: {
        type: Number,
        default: 50
    }
})

// Emits
//eslint-disable-next-line
const emit = defineEmits([
    'locate-client',
    'pin-client',
    'view-profile',
    'search-complete',
    'client-selected',
    'error'
])

// State
const searchQuery = ref("")
const searchResults = ref([])
const isLoading = ref(false)
const selectedClientId = ref(null)
const activeMoreId = ref(null)
const filterType = ref('all')
const recentSearches = ref([])
const pinnedClients = ref(new Set())
const currentPage = ref(1)
const hasMoreResults = ref(false)
const allResults = ref([])
allResults;

// Stores (if using Pinia/Vuex)
// // Computed
const filteredResults = computed(() => {
    let results = [...searchResults.value]

    switch (filterType.value) {
        case 'withLocation':
            results = results.filter(c => c.lat_client && c.lng_client)
            break
        case 'recent':
            results.sort((a, b) => new Date(b.lastlogoutdate_client) - new Date(a.lastlogoutdate_client))
            break
    }

    return results
})

// Methods
const getInitials = (name) => {
    if (!name) return '??'
    return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
}

const getAvatarColor = (client) => {
    // Status-based colors
    if (client.isonline_client === 1) {
        return '#10B981'; // Green - Online
    } else if (client.isonline_client === 0 && client.isactive_client === 1) {
        return '#6B7280'; // Gray - Offline but Active
    } else {
        return '#EF4444'; // Red - Offline and Inactive
    }
}


const getBalanceClass = (balance) => {
    if (balance > 1000) return 'high-balance'
    if (balance > 0) return 'medium-balance'
    return 'low-balance'
}

const isPinned = (clientId) => {
    return pinnedClients.value.has(clientId)
}

// Search function
const performSearch = async () => {
    //
    isLoading.value = true;
    const data = await clientService.search({ username_client: searchQuery.value });
    searchResults.value = data.clients;
    isLoading.value = false;
    emit('search-complete', searchQuery.value);
}


// Clear search
const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = []
    selectedClientId.value = null
    activeMoreId.value = null
}


// Action handlers
const locateClient = (client) => {
    if (client.lat_client && client.lng_client) {
        emit('locate-client', client)
    }
}

const pinClient = (client) => {
    // if (client.lng_client && client.lng_client) {
    //     if (pinnedClients.value.has(client.syspk_client)) {
    //         pinnedClients.value.delete(client.syspk_client)
    //         emit('pin-client', { action: 'remove', client })
    //     } else {
    //         pinnedClients.value.add(client.syspk_client)
    //         emit('pin-client', { action: 'add', client })
    //     }

    //     // Force reactivity
    //     pinnedClients.value = new Set(pinnedClients.value)
    // }
    emit('pin-client', { ...client });
}

const viewProfile = (client) => {
    selectedClientId.value = client.syspk_client
    emit('view-profile', client)

    // Navigate to profile page or open modal
    // router.push(`/clients/${client.id}`)
}

const viewTransactions = (client) => {
    console.log('View transactions for:', client)
    // Navigate to transactions
    // router.push(`/clients/${client.id}/transactions`)
}

const sendMessage = (client) => {
    console.log('Send message to:', client)
    // Open message modal
}

const addToFavorites = (client) => {
    console.log('Add to favorites:', client)
    // Add to favorites logic
}

const exportClientData = (client) => {
    console.log('Export client data:', client)
    // Export logic
}

const toggleMoreActions = (clientId) => {
    activeMoreId.value = activeMoreId.value === clientId ? null : clientId
}

const loadMore = async () => {
    currentPage.value++
    // Load more results
}

// Click outside to close dropdown
const handleClickOutside = (event) => {
    if (!event.target.closest('.more-actions')) {
        activeMoreId.value = null
    }
}


const initiateSearch = (skey) => {
    searchQuery.value = skey;
    performSearch();
}
// eslint-disable-next-line
defineExpose({ initiateSearch });


// Lifecycle
onMounted(() => {
    document.addEventListener('click', handleClickOutside)

    if (props.initialQuery) {
        performSearch()
    }
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.client-search-container {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.search-header {
    padding: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.search-title {
    margin: 0 0 15px 0;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.search-box {
    display: flex;
    gap: 10px;
}

.search-input {
    flex: 1;
    padding: 12px 16px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.3s;
}

.search-input:focus {
    outline: none;
    border-color: white;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
}

.search-input.loading {
    /* Add spinner SVG */
    background-repeat: no-repeat;
    background-position: right 12px center;
}

.search-button {
    padding: 12px 24px;
    background: white;
    color: #667eea;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    min-width: 100px;
}

.search-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.search-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.filter-options {
    margin-top: 15px;
}

.filter-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
}

.filter-select option {
    background: #667eea;
    color: white;
}

/* Search Results */
.search-results {
    max-height: 600px;
    overflow-y: auto;
}

.results-header {
    padding: 15px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #eef2f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.results-count {
    font-weight: 600;
    color: #333;
}

.clear-results {
    color: #667eea;
    cursor: pointer;
    font-size: 14px;
}

.clear-results:hover {
    text-decoration: underline;
}

.results-list {
    padding: 10px;
}

/* Client Card */
.client-card {
    display: flex;
    gap: 15px;
    padding: 15px;
    margin-bottom: 10px;
    background: white;
    border: 1px solid #eef2f6;
    border-radius: 12px;
    transition: all 0.3s;
    position: relative;
}

.client-card:hover {
    transform: translateX(5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
}

.client-card.selected {
    border-color: #667eea;
    background: #f0f4ff;
}

/* Avatar */
.client-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    font-size: 20px;
    flex-shrink: 0;
}

/* Client Info */
.client-info {
    flex: 1;
}

.client-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
}

.client-username {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}

.client-details {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 8px;
    font-size: 13px;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #555;
}

.detail-item.balance {
    padding: 2px 8px;
    border-radius: 12px;
    font-weight: 500;
}

.high-balance {
    background: #e6f7e6;
    color: #2ecc71;
}

.medium-balance {
    background: #fff3e0;
    color: #f39c12;
}

.low-balance {
    background: #fee7e7;
    color: #e74c3c;
}

.client-location {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
}

.client-location.no-location {
    color: #999;
    font-style: italic;
}

.location-icon {
    font-size: 14px;
}

/* Action Buttons */
.client-actions {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.action-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 6px 10px;
    border: none;
    border-radius: 8px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 60px;
}

.action-button.locate {
    background: #e3f2fd;
    color: #1976d2;
}

.action-button.locate:hover {
    background: #1976d2;
    color: white;
}

.action-button.pin {
    background: #fff3e0;
    color: #f57c00;
}

.action-button.pin:hover,
.action-button.pin.active {
    background: #f57c00;
    color: white;
}

.action-button.profile {
    background: #f3e5f5;
    color: #7b1fa2;
}

.action-button.profile:hover {
    background: #7b1fa2;
    color: white;
}

.button-icon {
    font-size: 16px;
    margin-bottom: 2px;
}

.button-text {
    font-size: 10px;
}

/* More Actions */
.more-actions {
    position: relative;
}

.more-button {
    width: 36px;
    height: 36px;
    border: 1px solid #eef2f6;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    transition: all 0.2s;
}

.more-button:hover {
    background: #f8f9fa;
    border-color: #667eea;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 5px;
    background: white;
    border: 1px solid #eef2f6;
    border-radius: 8px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 180px;
}

.dropdown-menu button {
    width: 100%;
    padding: 10px 15px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #333;
    transition: all 0.2s;
}

.dropdown-menu button:hover {
    background: #f8f9fa;
    color: #667eea;
}

/* Load More */
.load-more {
    padding: 20px;
    text-align: center;
}

.load-more-button {
    padding: 10px 24px;
    background: #f8f9fa;
    border: 2px solid #eef2f6;
    border-radius: 8px;
    color: #667eea;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
}

.load-more-button:hover {
    background: #667eea;
    border-color: #667eea;
    color: white;
}

/* Empty States */
.no-results,
.initial-state,
.loading-state {
    padding: 60px 20px;
    text-align: center;
    background: #f8f9fa;
}

.no-results-icon,
.initial-icon {
    font-size: 48px;
    margin-bottom: 20px;
    opacity: 0.5;
}

.no-results-text,
.initial-text {
    font-size: 18px;
    color: #333;
    margin-bottom: 15px;
}

.no-results-suggestions {
    color: #666;
    font-size: 14px;
}

.no-results-suggestions ul {
    list-style: none;
    padding: 0;
    margin: 10px 0 0;
}

.no-results-suggestions li {
    margin: 5px 0;
}

/* Recent Searches */
.recent-title {
    color: #666;
    margin-bottom: 10px;
    font-size: 14px;
}

.recent-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
}

.recent-tag {
    padding: 6px 12px;
    background: white;
    border: 1px solid #eef2f6;
    border-radius: 20px;
    font-size: 13px;
    color: #667eea;
    cursor: pointer;
    transition: all 0.2s;
}

.recent-tag:hover {
    background: #667eea;
    color: white;
    border-color: #667eea;
}

/* Loading States */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
}

.spinner,
.spinner-large {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.spinner {
    width: 20px;
    height: 20px;
    display: inline-block;
}

.spinner-large {
    width: 40px;
    height: 40px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Animations */
.result-list-enter-active,
.result-list-leave-active {
    transition: all 0.3s ease;
}

.result-list-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.result-list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.result-list-move {
    transition: transform 0.3s ease;
}

/* Responsive Design */
@media (max-width: 600px) {
    .client-card {
        flex-direction: column;
    }

    .client-avatar {
        align-self: center;
    }

    .client-actions {
        justify-content: center;
        flex-wrap: wrap;
    }

    .search-box {
        flex-direction: column;
    }

    .search-button {
        width: 100%;
    }
}
</style>